import { useEffect, useRef } from "react";

// Waiting for a split paragraph's correction: often at first, then less,
// until well past the backend's 10-minute correction lock. By then the
// backend reports a regeneration that died as failed.
export const FAST_POLL_MS = 3000;
export const SLOW_POLL_MS = 10000;
export const FAST_POLL_FOR_MS = 60 * 1000;
export const POLL_LIMIT_MS = 12 * 60 * 1000;

// While `active`, reads the paragraph with `read()` until its
// correction_status is no longer "pending" (or it is gone), then calls
// `onSettled()`, which should reload the data that drives `active`. Keeps
// asking in case that reload fails, and stops after POLL_LIMIT_MS with a
// last `onSettled()`. Always calls the latest `read` and `onSettled`.
export default function useCorrectionWait(active, read, onSettled) {
  const readRef = useRef(read);
  const onSettledRef = useRef(onSettled);
  readRef.current = read;
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!active) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      const elapsed = Date.now() - startedAt;
      const overdue = elapsed >= POLL_LIMIT_MS;
      let settled = overdue;
      if (!overdue) {
        try {
          const paragraph = await readRef.current();
          settled = !paragraph || paragraph.correction_status !== "pending";
        } catch (err) {
          // No answer is no result: ask again on the next tick.
        }
      }
      if (cancelled) return;
      if (settled) onSettledRef.current();
      if (overdue) return;
      timer = setTimeout(
        tick,
        elapsed < FAST_POLL_FOR_MS ? FAST_POLL_MS : SLOW_POLL_MS
      );
    };
    timer = setTimeout(tick, FAST_POLL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active]);
}
